import { Outlet, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { AppShellMainChrome } from '@/components/layout/AppShellMainChrome'
import { useAppShellChromeLayout } from '@/components/layout/useAppShellChromeLayout'
import { SidebarBackGuardProvider } from '@/contexts/SidebarBackGuardContext'
import { useAuth } from '@/lib/config/auth-provider'
import { useBrand } from '@/lib/brand-provider'
import { useTheme } from '@/lib/theme-provider'
import { useFavicon } from '@/hooks/useFavicon'
import { FONT_PRELOAD_HREFS } from '@/lib/theme/theme-presets'

const DEFAULT_TITLE = 'Fairgen'
const DEFAULT_FAVICON = '/icon.svg'

/**
 * React Router layout for authenticated main-app routes (sidebar + command palette).
 * Public/auth/share/internal routes are siblings — no pathname-based chrome skipping.
 */
export function SpaAppShellLayout() {
  const { pathname } = useLocation()
  const { user: authUser, isAuthenticated: isAuthed, isLoading: authLoading } = useAuth()
  const { brandIconLogoUrl, isLoading: brandLoading } = useBrand()
  const { fontFamily } = useTheme()
  const isBackofficePage = pathname.startsWith('/explore')

  const {
    mounted,
    isCollapsed,
    mobileNavOpen,
    setMobileNavOpen,
    toggleSidebar,
    isMinimalChrome,
  } = useAppShellChromeLayout(pathname)

  const whiteLabelEnabled = authUser?.featureFlags?.whiteLabel === true
  const orgName = (authUser?.organizationName ?? authUser?.companyName)?.trim() || null
  const titleText = orgName ? `${orgName} Twins` : DEFAULT_TITLE
  // `brandFaviconUrl` is not yet a field — fall back to the icon logo for v1.
  const faviconHref = whiteLabelEnabled && brandIconLogoUrl ? brandIconLogoUrl : DEFAULT_FAVICON
  const fontPreloadHref = fontFamily ? FONT_PRELOAD_HREFS[fontFamily] : null

  useFavicon(faviconHref)

  if (!mounted) {
    return <div className="min-h-screen bg-background" />
  }

  // Wait for both auth and brand tokens before painting app content.
  // Brand tokens (CSS variables) are applied in BrandProvider's useEffect — which
  // runs after the auth effect resolves. Without this gate, the app shell unmasks
  // one render cycle before brand tokens are written, causing a visible flash of
  // Fairgen default colors on the first authenticated page load.
  if (authLoading || !isAuthed || brandLoading) {
    return <div className="min-h-screen bg-background" />
  }

  return (
    <>
      <Helmet>
        <title>{titleText}</title>
        {fontPreloadHref ? (
          <link
            rel="preload"
            as="font"
            type="font/woff2"
            href={fontPreloadHref}
            crossOrigin="anonymous"
          />
        ) : null}
      </Helmet>
      <SidebarBackGuardProvider>
        <AppShellMainChrome
          mounted={mounted}
          isCollapsed={isCollapsed}
          toggleSidebar={toggleSidebar}
          mobileNavOpen={mobileNavOpen}
          setMobileNavOpen={setMobileNavOpen}
          isBackofficePage={isBackofficePage}
          isMinimalChrome={isMinimalChrome}
        >
          <Outlet />
        </AppShellMainChrome>
      </SidebarBackGuardProvider>
    </>
  )
}
